import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Box, InputAdornment } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CustomModal from '../../../../components/mui/CustomModal';
import CustomTextField from '../../../../components/mui/CustomTextField';
import CustomTypography from '../../../../components/mui/CustomTypography';
import {
  GLOSSARY_PAGE_SIZE,
  GLOSSARY_SORT_HINTS,
  GLOSSARY_SORT_LABELS,
  GLOSSARY_SORT_MODES,
} from '../../../../constants/GlossaryConstants';
import useKeyPress from '../../../../hook/useKeyPress';
import {
  closeGlossaryModal,
  cycleGlossarySort,
  glossaryListRequest,
  nextGlossaryPage,
  retryGlossaryList,
  setGlossarySearch,
} from '../actions/glossaryActions';
import Loading from '../components/loading';

const SEARCH_DEBOUNCE_MS = 500;

const SORT_ICONS = {
  default: SwapVertIcon,
  nameAsc: ArrowUpwardIcon,
  nameDesc: ArrowDownwardIcon,
};

const GlossaryModal = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { isOpen, loading, items, totalCount, page, search, sortMode, hasMore, hasError, retryToken } = useSelector(
    (state) => state.glossary.list,
  );

  const [searchInput, setSearchInput] = useState(search);
  const [lockedHeight, setLockedHeight] = useState(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const handleClose = useCallback(() => dispatch(closeGlossaryModal()), [dispatch]);
  useKeyPress(handleClose, 'Escape', false, false);

  // isOpen is a dependency, not just a guard, so reopening re-fires page 1 even
  // when page/search/sort are already back at their defaults.
  useEffect(() => {
    if (!isOpen || !id) return;
    const sort = GLOSSARY_SORT_MODES[sortMode];
    dispatch(
      glossaryListRequest({
        page,
        rowNumber: GLOSSARY_PAGE_SIZE,
        sortRowName: sort.sortRowName,
        sortRowDirection: sort.sortRowDirection,
        audilfileId: id,
        search,
      }),
    );
  }, [isOpen, id, page, search, sortMode, retryToken, dispatch]);

  // Hand-rolled debounce (the repo idiom — see AllFiles.js); lodash is not a
  // declared dependency here. The unmount cleanup matters: the section mounts
  // this modal conditionally, so closing mid-keystroke would otherwise dispatch
  // a search into a store whose modal is already gone.
  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchInput(value); // local state keeps typing responsive
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => dispatch(setGlossarySearch(value)), SEARCH_DEBOUNCE_MS);
  };

  // Measure the list once the first unfiltered page has rendered and hold that
  // height for the rest of the modal's life. Without it, a search matching two
  // or three terms shrinks the box and the whole modal jumps. Measuring beats
  // a fixed height because it adapts to a small glossary and to wrapped rows.
  useEffect(() => {
    if (lockedHeight !== null || loading || items === null || !scrollRef.current) return;
    const height = scrollRef.current.getBoundingClientRect().height;
    if (height > 0) setLockedHeight(height);
  }, [loading, items, lockedHeight]);

  // items?.length is in the deps so the observer re-arms after each page lands —
  // otherwise a short first page leaves the sentinel visible but never retriggered.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        // Safe to fire repeatedly: the reducer no-ops until the current page
        // has fully arrived.
        if (entries[0]?.isIntersecting) dispatch(nextGlossaryPage());
      },
      { root: scrollRef.current, rootMargin: '200px', threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, hasError, items?.length, dispatch]);

  const SortIcon = SORT_ICONS[sortMode];
  const isSortActive = sortMode !== 'default';
  const isInitialLoad = items === null;
  const isEmpty = items !== null && items.length === 0;
  // Search / sort re-query with rows still on screen: dim them instead of
  // swapping in a loader, which would collapse the modal and make it jump.
  const isRefreshing = loading && !isInitialLoad && page === 1;

  return (
    <CustomModal open={isOpen} onClose={handleClose} sx={{ maxWidth: '600px', padding: '0' }}>
      <div>
        <div className="preview-dropdown-header">
          {/* Title and count share one flex child, else space-between pushes
              the count into the middle of the header. */}
          <span className="glossaryHeaderTitle">
            Glossary
            {totalCount > 0 && <span className="glossaryHeaderCount">({totalCount})</span>}
          </span>
          <HighlightOffRoundedIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', opacity: 0.5, height: '28px', width: '28px' }}
          />
        </div>
        <div className="preview-dropdown-body">
          <div className="glossaryToolbar">
            <CustomTextField
              fullWidth
              variant="outlined"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search terms..."
              autoFocus
              // InputProps, not slotProps: this repo is on MUI v5, where
              // TextField has no slotProps and the adornment would be dropped.
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: '20px', opacity: 0.5 }} />
                  </InputAdornment>
                ),
              }}
            />
            <button
              type="button"
              className={`glossarySortBtn${isSortActive ? ' active' : ''}`}
              onClick={() => dispatch(cycleGlossarySort())}
              title={GLOSSARY_SORT_HINTS[sortMode]}
              aria-pressed={isSortActive}
            >
              <SortIcon sx={{ fontSize: '18px' }} />
              {GLOSSARY_SORT_LABELS[sortMode]}
            </button>
          </div>

          <div
            ref={scrollRef}
            className={`glossaryModalList custom-scroll${isRefreshing ? ' refreshing' : ''}`}
            style={lockedHeight ? { minHeight: `${lockedHeight}px` } : undefined}
          >
            {isInitialLoad && loading && <Loading sx={{ height: '200px' }} />}

            {isInitialLoad && !loading && hasError && (
              <div className="glossaryEmpty">
                <CustomTypography size="mediumSmall">Could not load the glossary.</CustomTypography>
                <button type="button" className="glossaryRetryBtn" onClick={() => dispatch(retryGlossaryList())}>
                  Try again
                </button>
              </div>
            )}

            {isEmpty && !loading && (
              <div className="glossaryEmpty">
                <CustomTypography size="mediumSmall">
                  {search ? 'No terms match your search.' : 'No glossary terms for this file.'}
                </CustomTypography>
              </div>
            )}

            {items?.map((term, index) => (
              <div className="glossaryRow" key={term?.Id ?? `glossary-${index}`}>
                {term?.Name}
              </div>
            ))}

            {/* Rendered only while more pages exist, so an exhausted or empty
                list produces no observer callbacks at all. */}
            {hasMore && !hasError && <div ref={sentinelRef} style={{ height: '1px' }} />}

            {loading && page > 1 && <Loading sx={{ height: '48px' }} />}

            {hasError && !isInitialLoad && (
              <Box className="glossaryRetryRow">
                <CustomTypography size="small">Could not load the rest of your terms.</CustomTypography>
                <button type="button" className="glossaryRetryBtn" onClick={() => dispatch(retryGlossaryList())}>
                  Try again
                </button>
              </Box>
            )}
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default GlossaryModal;
